/* Pulsio Header controller.
   Chrome-level interactions only in this build; mega-menu tab switching
   and search input handling land in the final JS chunk. */

(function () {
  'use strict';

  if (customElements.get('pulsio-header')) return;

  class PulsioHeader extends HTMLElement {
    /* CSS uses --pulsio-bar-transform-dur (default 0s) for the bar and panels
       transform transition so scroll-driven --pulsio-bar-translate updates are
       instant. Only data-scroll-state (top/scrolled/hidden) and data-mobile-state
       (drawer-open/search-open on mobile) actually alter the bar's transform —
       for those we bump the var to 180ms so the state morph animates. Other
       state attrs (page-scrolled, panel-open, search-open) only move `top`
       (which has its own 180ms transition), and enabling a transform window
       for them would let scroll-driven --pulsio-bar-translate updates lerp
       over 180ms — re-introducing the gap-then-jump lag. */
    static get observedAttributes() {
      return ['data-scroll-state', 'data-mobile-state'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;
      // Skip the initial state-attribute sets that fire during connectedCallback
      // (wireScrollState's initial onScroll) so the bar isn't briefly stuck in a
      // 180ms transition window right after page load.
      if (!this._pulsioReady) return;
      this.style.setProperty('--pulsio-bar-transform-dur', '180ms');
      clearTimeout(this._barTransformDurResetTimer);
      this._barTransformDurResetTimer = setTimeout(() => {
        this.style.removeProperty('--pulsio-bar-transform-dur');
      }, 220);
    }

    constructor() {
      super();
      this.openMenuId = null;
      this.onDocClick = this.onDocClick.bind(this);
      this.onKey = this.onKey.bind(this);
    }

    connectedCallback() {
      this.bar = this.querySelector('.pulsio-header__bar');
      this.backdrop = this.querySelector('[data-backdrop]');
      this.panels = this.querySelectorAll('[data-panel-for]');
      this.triggers = this.querySelectorAll('[data-menu-trigger]');
      this.searchTrigger = this.querySelector('[data-search-trigger]');
      this.searchPanel = this.querySelector('#pulsio-search');
      this.mobileDrawer = this.querySelector('#pulsio-mobile-drawer');
      this.mobileSearch = this.querySelector('#pulsio-mobile-search');
      this.hamburger = this.querySelector('.pulsio-header__hamburger');
      this.cartLink = this.querySelector('[data-cart-trigger]');

      this.wireDesktopMenuToggle();
      this.wireDesktopMenuHover();
      this.wireMobileTriggers();
      this.wireSearchTrigger();
      this.wireSearchPanel(this.searchPanel, 'pulsio-predictive-search');
      this.wireSearchPanel(this.mobileSearch, 'pulsio-predictive-search-mobile');
      this.wireBackdrop();
      this.wireCartObserver();
      this.wireCartCount();
      this.wireShopTabs();
      this.wireDrawerPills();
      this.wireInlineSearch();
      this.wireBarSearch();
      this.wireDrawerAccordions();
      this.wireRegionSheet();
      this.wireScrollState();
      this.wireViewportTransition();
      this.wireTopReserve();

      // Mark ready after the current frame so the initial state-attribute sets
      // above don't enable the transform transition (see attributeChangedCallback).
      requestAnimationFrame(() => { this._pulsioReady = true; });

      document.addEventListener('keydown', this.onKey);
      document.addEventListener('click', this.onDocClick);

      // A menu link to the page you're already on only changes the #part of the address, so the page updated behind
      // the open menu and the tap looked dead (Justyn 17/10/2026, "View all Best Sellers ... seems to not do
      // anything" on the collection page). Such links now close the menu (and any panel or search) as they go.
      this.addEventListener('click', (e) => {
        const a = e.target.closest('a[href]');
        if (!a || e.defaultPrevented || a.target === '_blank') return;
        let url;
        try { url = new URL(a.href, location.href); } catch (err) { return; }
        if (!url.hash || url.origin !== location.origin || url.pathname !== location.pathname || url.search !== location.search) return;
        // Instant, so the page is unpinned before the browser follows the #link and the page reacts to it.
        this.closeMenu();
        this.closeSearch();
        this.closeMobile({ instant: true });
        this.showBackdrop(false);
      });
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('click', this.onDocClick);
      if (this.desktopMQ && this.desktopMQHandler) {
        this.desktopMQ.removeEventListener('change', this.desktopMQHandler);
      }
    }

    /* ------------------------------------------------------------------
       Viewport transition — close any mobile-only overlays when the
       viewport crosses into desktop. Without this, mobile drawer/search
       state attributes survive the resize and pollute the desktop layout.
       ------------------------------------------------------------------ */
    wireViewportTransition() {
      this.desktopMQ = window.matchMedia('(min-width: 1024px)');
      this.desktopMQHandler = (e) => {
        if (e.matches) this.closeMobile();
        else if (this.openMenuId) {
          // Crossing INTO mobile: a desktop-opened mega menu has no close
          // affordance there, so retract it.
          this.closeMenu();
          this.showBackdrop(false);
        }
      };
      this.desktopMQ.addEventListener('change', this.desktopMQHandler);
    }

    /* ------------------------------------------------------------------
       Desktop mega menu — burger click-toggle. The burger in the bar's left
       cluster is the only [data-menu-trigger]; clicking it toggles its panel.
       No hover-intent: the menu opens/closes on click only (Esc, outside
       click, bar-strip click and backdrop click still dismiss it).
       ------------------------------------------------------------------ */
    wireDesktopMenuToggle() {
      this.triggers.forEach((trigger) => {
        const id = trigger.getAttribute('data-menu-trigger');
        const panel = this.querySelector(`[data-panel-for="${id}"]`);
        if (!panel) return;

        trigger.addEventListener('click', (e) => {
          // The bar titles (Shop, Explore, Gift Guide, Treatment Area) only ever open their menu, never go to
          // their collection page (Justyn 17/10/2026). A click on a menu hover already opened keeps it open and
          // stops it closing when the mouse leaves; a second click closes it.
          e.preventDefault();
          const isMenuLink = trigger.classList.contains('pulsio-header__nav-link--menu');
          if (isMenuLink && this.openMenuId === id && this._openedByHover) {
            this._openedByHover = false;
            clearTimeout(this._hoverCloseTimer);
            return;
          }
          if (this.openMenuId === id) this.closeMenu();
          else this.openMenu(id);
        });
      });
    }

    /* ------------------------------------------------------------------
       Desktop hover-intent — fine pointers only. Resting on the Shop link
       opens its panel after a short delay; moving off the header (onto the
       backdrop) closes it after a grace period so a diagonal path from the
       link down into the panel never drops it. Touch and coarse pointers
       keep click-only behaviour.
       ------------------------------------------------------------------ */
    wireDesktopMenuHover() {
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      const links = this.querySelectorAll('.pulsio-header__nav-link--menu[data-menu-trigger]');
      if (!links.length) return;
      const OPEN_DELAY = 90;
      const CLOSE_DELAY = 260;
      const isDesktop = () => window.matchMedia('(min-width: 1024px)').matches;
      const cancelClose = () => clearTimeout(this._hoverCloseTimer);

      /* 16/10: closing the menu changes the bar's shape, which can slide a menu link under a mouse that hasn't
         moved. The browser reports that as the pointer entering the link and the menu reopened by itself. Hover
         now opens only on real mouse movement over a link (layout-shift events carry no movement), and never in
         the moment just after a close. */
      const REOPEN_COOLDOWN = 450;
      const startOpen = (id) => {
        if (!isDesktop()) return;
        if (Date.now() - (this._menuClosedAt || 0) < REOPEN_COOLDOWN) return;
        cancelClose();
        if (this.openMenuId === id || this._hoverPendingId === id) return;
        clearTimeout(this._hoverOpenTimer);
        this._hoverPendingId = id;
        this._hoverOpenTimer = setTimeout(() => {
          this._hoverPendingId = null;
          if (this.openMenuId !== id) this.openMenu(id, { byHover: true });
        }, OPEN_DELAY);
      };
      links.forEach((link) => {
        const id = link.getAttribute('data-menu-trigger');
        link.addEventListener('pointermove', (e) => {
          if (e.pointerType && e.pointerType !== 'mouse') return;
          if (!e.movementX && !e.movementY) return;
          startOpen(id);
        });
        link.addEventListener('pointerenter', () => { if (this.openMenuId) cancelClose(); });
        link.addEventListener('pointerleave', () => {
          clearTimeout(this._hoverOpenTimer);
          this._hoverPendingId = null;
        });
      });

      const canvas = this.querySelector('.pulsio-header__panels');
      [this.bar, canvas].forEach((el) => {
        if (el) el.addEventListener('pointerenter', cancelClose);
      });
      if (this.backdrop) {
        this.backdrop.addEventListener('pointerenter', () => {
          if (!this.openMenuId || !this._openedByHover) return;
          cancelClose();
          this._hoverCloseTimer = setTimeout(() => this.closeMenu(), CLOSE_DELAY);
        });
      }
    }

    openMenu(id, opts = {}) {
      if (this.openMenuId === id) return;
      this._openedByHover = !!opts.byHover;

      const wasOpen = this.hasAttribute('data-panel-open');

      // Cross-fade out any currently-open panel without removing data-panel-open
      // (kept on the host so the bar's ::before cream sheet stays in place
      // through the swap rather than retracting + re-descending).
      this.panels.forEach((p) => {
        if (!p.hidden) this.animatePanelOut(p);
      });
      this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      this.closeSearch();

      const panel = this.querySelector(`[data-panel-for="${id}"]`);
      const triggers = this.querySelectorAll(`[data-menu-trigger="${id}"]`);
      if (!panel || !triggers.length) return;

      // If this panel was mid-close (rapid re-open), cancel the pending hide
      // and clear the closing class so the entrance animation plays.
      if (panel._hideTimer) {
        clearTimeout(panel._hideTimer);
        panel._hideTimer = null;
      }
      panel.classList.remove('is-closing');
      panel.hidden = false;
      triggers.forEach((t) => t.setAttribute('aria-expanded', 'true'));
      this.openMenuId = id;
      this.setAttribute('data-panel-open', 'true');
      // Don't force the scroll state to "top" here — if the user opens the
      // menu while scrolled, the bar should stay at its scrolled position
      // (top: 29px pill) rather than morph back to its top-of-page position
      // and back again on close. The bar's bg/shadow/blur still morph to
      // cream via the [data-panel-open] CSS, but geometry stays put.
      this.showBackdrop(true);

      // Fresh open (canvas was closed): hold the panel content invisible while
      // the cream canvas slides down, then let the items cascade in once the
      // slide AND the parent fade have both settled. The attribute drives the
      // staggered CSS delays for the items (~650ms + per-item offset), so it
      // has to stay set through the entire cascade — otherwise the CSS rule
      // stops matching and the items snap to their final state mid-animation.
      // 2000ms covers up to ~30 items at the current 40ms-per-index spread.
      if (!wasOpen) {
        this.setAttribute('data-panels-just-opening', 'true');
        clearTimeout(this._panelsJustOpeningTimer);
        this._panelsJustOpeningTimer = setTimeout(() => {
          this.removeAttribute('data-panels-just-opening');
        }, 2000);
      } else if (this.hasAttribute('data-panels-just-opening')) {
        // Swap that lands while the initial open is still cascading — drop
        // the hold so the incoming panel cross-fades immediately instead of
        // inheriting the fresh-open delay.
        this.removeAttribute('data-panels-just-opening');
        clearTimeout(this._panelsJustOpeningTimer);
      }
    }

    closeMenu(opts = {}) {
      if (this.openMenuId) this._menuClosedAt = Date.now();
      clearTimeout(this._hoverOpenTimer);
      this._hoverPendingId = null;
      this.panels.forEach((p) => {
        if (!p.hidden) this.animatePanelOut(p);
      });
      this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      this.openMenuId = null;
      if (!opts.keepBackdrop) this.showBackdrop(false);

      // Remove data-panel-open immediately so the bar's ::before cream sheet
      // retracts upward in sync with the panel's slide-out, rather than
      // waiting for the panel to finish closing first. Previously this was
      // deferred to avoid a dual-motion feel when the bar itself morphed
      // (full-width cream → pill glass) on close — that morph is gone now,
      // so deferral would just leave the cream sheet stranded over the bar
      // long after the panel has retreated.
      this.removeAttribute('data-panel-open');
      const y = window.scrollY;
      if (y > 24) this.setAttribute('data-scroll-state', 'scrolled');
      else this.setAttribute('data-scroll-state', 'top');
    }

    /* Run the fade-out animation, then actually hide the panel. Duration
       must match the CSS .pulsio-panel.is-closing animation (250ms,
       pulsio-mega-fade-out). */
    animatePanelOut(panel) {
      if (!panel || panel.hidden) return;
      if (panel._hideTimer) clearTimeout(panel._hideTimer);
      panel.classList.add('is-closing');
      panel._hideTimer = setTimeout(() => {
        panel.hidden = true;
        panel.classList.remove('is-closing');
        panel._hideTimer = null;
      }, 250);
    }

    /* ------------------------------------------------------------------
       Mobile drawer + hamburger
       ------------------------------------------------------------------ */
    wireMobileTriggers() {
      if (!this.hamburger) return;
      this.hamburger.addEventListener('click', () => {
        const state = this.getAttribute('data-mobile-state');
        if (state === 'drawer-open' || state === 'search-open') {
          this.closeMobile();
        } else {
          this.openMobileDrawer();
        }
      });
    }

    /* Page scroll lock. overflow:hidden on <body> is ignored by iOS Safari,
       so a swipe on an overlay with nothing left to scroll moved the page
       behind it. Pin the body in place (position:fixed at the current
       scroll offset) and put it back on unlock. */
    _lockScroll() {
      if (this._scrollLocked) return;
      this._scrollLocked = true;
      this._lockY = window.scrollY || 0;
      const b = document.body.style;
      b.overflow = 'hidden';
      b.position = 'fixed';
      b.top = `-${this._lockY}px`;
      b.left = '0';
      b.right = '0';
      b.width = '100%';
    }

    _unlockScroll() {
      if (!this._scrollLocked) return;
      this._scrollLocked = false;
      const b = document.body.style;
      b.overflow = ''; b.position = ''; b.top = ''; b.left = ''; b.right = ''; b.width = '';
      window.scrollTo(0, this._lockY || 0);
      this._scrollRestoring = true;
      requestAnimationFrame(() => requestAnimationFrame(() => { this._scrollRestoring = false; }));
    }

    openMobileDrawer() {
      clearTimeout(this._closeTimer);
      this.removeAttribute('data-mobile-closing');
      if (this.mobileDrawer) { this.mobileDrawer.removeAttribute('data-closing'); this.mobileDrawer.hidden = false; }
      this._ensureFirstDrawerAccOpen();
      this.setAttribute('data-mobile-state', 'drawer-open');
      this.hamburger.setAttribute('aria-expanded', 'true');
      this.showBackdrop(true);
      // Pin the page once the 280ms opening has played: pinning it (body position: fixed) relays out the whole
      // page, which on an iPhone landed on the first frames of the animation as a hitch. The menu covers the page
      // and contains its own scroll meanwhile.
      clearTimeout(this._lockTimer);
      this._lockTimer = setTimeout(() => { if (this.getAttribute('data-mobile-state')) this._lockScroll(); }, 300);
    }

    /* The drawer should never open fully collapsed — the first category
       (Best Sellers) renders open server-side; if the user collapsed
       everything last time, re-open it. Set instantly (no height animation)
       since it happens while the drawer itself is still revealing. */
    _ensureFirstDrawerAccOpen() {
      if (!this.mobileDrawer) return;
      if (this.mobileDrawer.querySelector('.pulsio-drawer-acc[open]:not([hidden])')) return;
      const first = this.mobileDrawer.querySelector('[data-drawer-default]') || this.mobileDrawer.querySelector('.pulsio-drawer-acc');
      if (!first) return;
      const pane = first.closest('[data-drawer-pane]') || this.mobileDrawer;
      this._showDrawerList(pane, first, true);
      this._syncDrawerPills(first.hasAttribute('data-drawer-default') ? null : first.getAttribute('data-drawer-acc'));
    }

    /* Show one product list in the phone menu's Shop pane and hide the rest, then bring the drawer back to
       the top so the list starts just under the pills. */
    _showDrawerList(pane, target, instant) {
      pane.querySelectorAll('.pulsio-drawer-acc').forEach((acc) => {
        const on = acc === target;
        acc.hidden = !on;
        acc.open = on;
        const body = acc.querySelector('.pulsio-drawer-acc__body');
        if (body) { body.style.height = ''; delete body.dataset.animating; delete body.dataset.closing; }
      });
      const scroller = this.mobileDrawer;
      if (scroller && !instant) {
        const smooth = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        scroller.scrollTo({ top: 0, behavior: smooth });
      }
    }

    openMobileSearch(opts = {}) {
      if (this.mobileSearch) this.mobileSearch.hidden = false;
      this.setAttribute('data-mobile-state', 'search-open');
      this.showBackdrop(true);
      this._lockScroll();
      if (opts.keepFocus) return;
      // Type in the top-row box when it's there; the panel's own box is the fallback.
      const input = (this.inlineSearch && this.inlineSearch.offsetParent) ? this.inlineSearch
        : (this.mobileSearch && this.mobileSearch.querySelector('[data-search-input]'));
      if (input) input.focus();
    }

    closeMobile(opts = {}) {
      if (this.inlineSearch) {
        this.inlineSearch.value = '';
        this.inlineSearch.blur();
        const panelInput = this.mobileSearch && this.mobileSearch.querySelector('[data-search-input]');
        if (panelInput && panelInput.value) { panelInput.value = ''; panelInput.dispatchEvent(new Event('input', { bubbles: true })); }
      }
      // Phones (Justyn 17/10/2026, "opening and closing not as smooth or quick as it could be"): the menu fades
      // and lifts away in 200ms, and the page is unpinned only after that, so the relayout never lands mid-motion.
      const drawer = this.mobileDrawer;
      const animate = !opts.instant && drawer && !drawer.hidden && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      clearTimeout(this._lockTimer);
      if (this.mobileSearch) this.mobileSearch.hidden = true;
      if (this._closeRegionSheet) this._closeRegionSheet();
      // data-mobile-closing keeps the bar's colour fade cheap while the menu goes (see pulsio-nav.css).
      if (animate) {
        this.setAttribute('data-mobile-closing', '');
        // Come back as the visible bar for where the page is, never tucked away: it was just tapped.
        const y = this._scrollLocked ? this._lockY : window.scrollY;
        this.setAttribute('data-scroll-state', y > 24 ? 'scrolled' : 'top');
      }
      this.removeAttribute('data-mobile-state');
      if (this.hamburger) this.hamburger.setAttribute('aria-expanded', 'false');
      this.showBackdrop(false);
      if (animate) {
        drawer.setAttribute('data-closing', '');
        clearTimeout(this._closeTimer);
        this._closeTimer = setTimeout(() => {
          drawer.hidden = true;
          drawer.removeAttribute('data-closing');
          this.removeAttribute('data-mobile-closing');
          this._unlockScroll();
        }, 200);
      } else {
        if (drawer) drawer.hidden = true;
        this._unlockScroll();
      }
    }

    /* ------------------------------------------------------------------
       Search trigger — opens desktop overlay or mobile search depending on viewport
       ------------------------------------------------------------------ */
    wireSearchTrigger() {
      if (!this.searchTrigger) return;
      this.searchTrigger.addEventListener('click', (e) => {
        e.preventDefault();
        const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
        if (isDesktop) {
          this.toggleSearch();
        } else {
          // The search icon stays in the bar while the drawer is open; hand
          // straight over to search without dropping backdrop or scroll-lock.
          if (this.mobileDrawer) this.mobileDrawer.hidden = true;
          this.openMobileSearch();
        }
      });
    }

    /* Computer search field in the bar (17/10/2026): focusing it opens the search panel below without moving
       the caret, typing mirrors into the panel's own input (which runs the predictive search), Enter searches.
       The panel's own input row is hidden while the bar field drives it (data-bar-search on the host). */
    wireBarSearch() {
      const box = this.querySelector('[data-bar-search-input]');
      if (!box || !this.searchPanel) return;
      this.barSearch = box;
      const panelInput = this.searchPanel.querySelector('[data-search-input]');
      const form = this.searchPanel.querySelector('[data-search-form]');
      box.addEventListener('focus', () => {
        if (!window.matchMedia('(min-width: 1024px)').matches) return;
        this.setAttribute('data-bar-search', '');
        if (this.searchPanel.hidden) this.toggleSearch({ keepFocus: true });
      });
      box.addEventListener('input', () => {
        if (!panelInput) return;
        panelInput.value = box.value;
        panelInput.dispatchEvent(new Event('input', { bubbles: true }));
      });
      box.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' || !form) return;
        e.preventDefault();
        if (panelInput) panelInput.value = box.value;
        form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true }));
      });
    }

    toggleSearch(opts = {}) {
      if (!this.searchPanel) return;
      const isOpen = !this.searchPanel.hidden;
      if (isOpen) {
        this.closeSearch();
        this.showBackdrop(false);
      } else {
        this.closeMenu({ keepBackdrop: true });
        this.searchPanel.hidden = false;
        this.searchTrigger.setAttribute('aria-expanded', 'true');
        this.setAttribute('data-search-open', 'true');
        this.setAttribute('data-scroll-state', 'top');
        this.showBackdrop(true);
        this._lockScroll();
        const input = this.searchPanel.querySelector('input[type="search"]');
        if (input && !opts.keepFocus) input.focus();
      }
    }

    closeSearch() {
      if (!this.searchPanel || this.searchPanel.hidden) return;
      this.searchPanel.hidden = true;
      if (this.searchTrigger) this.searchTrigger.setAttribute('aria-expanded', 'false');
      this.removeAttribute('data-search-open');
      this.removeAttribute('data-bar-search');
      if (this.barSearch) {
        this.barSearch.value = '';
        if (document.activeElement === this.barSearch) this.barSearch.blur();
        const panelInput = this.searchPanel.querySelector('[data-search-input]');
        if (panelInput && panelInput.value) { panelInput.value = ''; panelInput.dispatchEvent(new Event('input', { bubbles: true })); }
      }
      const y = window.scrollY;
      if (y > 24) this.setAttribute('data-scroll-state', 'scrolled');
      else this.setAttribute('data-scroll-state', 'top');
      if (!this.hasAttribute('data-mobile-state')) this._unlockScroll();
    }

    /* ------------------------------------------------------------------
       Search panel wiring — handles input, Clear button, popular-topic
       clicks, and predictive search swap for a given panel (desktop or
       mobile). State stored under `panel.__pulsioSearch` so each panel
       keeps its own popular-results snapshot.
       ------------------------------------------------------------------ */
    wireSearchPanel(panel, sectionId) {
      if (!panel) return;
      const input = panel.querySelector('[data-search-input]');
      if (!input) return;
      const container = panel.querySelector('[data-results-container]');
      const clearBtn = panel.querySelector('[data-search-clear]');

      const state = {
        sectionId,
        container,
        clearBtn,
        debounceTimer: null,
        popularSnapshot: null,
      };
      panel.__pulsioSearch = state;

      const initial = container && container.querySelector('[data-search-results]');
      if (initial) state.popularSnapshot = initial.cloneNode(true);

      const setTyping = (typing) => {
        if (typing) panel.setAttribute('data-typing', 'true');
        else panel.removeAttribute('data-typing');
        if (clearBtn) clearBtn.hidden = !typing;
      };

      input.addEventListener('input', () => {
        const q = input.value.trim();
        clearTimeout(state.debounceTimer);
        if (q.length === 0) {
          setTyping(false);
          this.restorePopularResults(panel);
          return;
        }
        setTyping(true);
        state.debounceTimer = setTimeout(() => this.runPredictiveSearch(panel, q), 200);
      });

      if (clearBtn) {
        clearBtn.addEventListener('click', (e) => {
          e.preventDefault();
          input.value = '';
          setTyping(false);
          this.restorePopularResults(panel);
          input.focus();
        });
      }

      panel.addEventListener('click', (e) => {
        const topic = e.target.closest('[data-search-topic]');
        if (!topic) return;
        e.preventDefault();
        const term = topic.getAttribute('data-search-topic') || '';
        input.value = term;
        input.focus();
        setTyping(term.length > 0);
        this.runPredictiveSearch(panel, term);
      });

      // Intercept Enter / form submit so the user stays inside the overlay
      // and the predictive results render in-place. Without this the form
      // submits to /search and the page navigates away from the open panel.
      const form = panel.querySelector('[data-search-form]');
      if (form) {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          const q = input.value.trim();
          clearTimeout(state.debounceTimer);
          if (q.length === 0) {
            setTyping(false);
            this.restorePopularResults(panel);
            return;
          }
          setTyping(true);
          this.runPredictiveSearch(panel, q);
        });
      }
    }

    getCategorySearchFallback(panel, query) {
      const isMobile = panel.classList.contains('pulsio-mobile-search');
      const store = this.querySelector(
        `[data-search-category-fallbacks="${isMobile ? 'mobile' : 'desktop'}"]`
      );
      if (!store) return null;

      const q = query.toLowerCase().trim();
      if (!q) return null;

      let bestTemplate = null;
      let bestScore = 0;

      store.querySelectorAll('template[data-keywords]').forEach((tpl) => {
        const keywords = (tpl.getAttribute('data-keywords') || '').split('|');
        keywords.forEach((raw) => {
          const k = raw.trim().toLowerCase();
          if (!k) return;
          if (q === k || q.includes(k) || k.includes(q)) {
            const score = k.length;
            if (score > bestScore) {
              bestTemplate = tpl;
              bestScore = score;
            }
          }
        });
      });

      if (!bestTemplate) return null;
      const node = bestTemplate.content.querySelector('[data-search-results]');
      return node ? node.cloneNode(true) : null;
    }

    hasSearchProductResults(resultsEl) {
      if (!resultsEl) return false;
      return Boolean(resultsEl.querySelector('.pulsio-card, .pulsio-drawer-prod'));
    }

    swapSearchResults(state, fresh) {
      const current = state.container.querySelector('[data-search-results]');
      if (current) current.replaceWith(fresh);
      else state.container.appendChild(fresh);
    }

    /* Search phrase map (Justyn 17/10/2026: "make the site search understand therapies, needs and body parts").
       snippets/pulsio-search-intents.liquid lists, for each gift centre group, the words people type and the
       group's products. A typed phrase that matches shows those products, with a link to the group's page,
       instead of Shopify's word match (which turned "back" into Hips, Air, TENS Pod and Compression). */
    _searchIntents() {
      if (this._intents !== undefined) return this._intents;
      const el = this.querySelector('[data-search-intents]');
      try {
        this._intents = el ? JSON.parse(el.textContent) : [];
        this._intentCentre = el ? el.getAttribute('data-centre') : '';
      } catch (e) { this._intents = []; }
      return this._intents;
    }

    _matchIntent(query) {
      const norm = (t) => String(t).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^\p{L}\p{N}& ]+/gu, ' ').replace(/\s+/g, ' ').trim();
      const q = norm(query);
      if (q.length < 2) return null;
      const words = ` ${q} `;
      let best = null; let bestScore = 0;
      this._searchIntents().forEach((intent) => {
        intent.keys.forEach((k0) => {
          const k = norm(k0);
          let score = 0;
          if (q === k) score = 300 + k.length;
          else if (words.includes(` ${k} `)) score = 200 + k.length;
          else if (q.length >= 3 && k.startsWith(q)) score = 100 + q.length;
          if (score > bestScore) { bestScore = score; best = intent; }
        });
      });
      return best;
    }

    async _searchPool() {
      if (this._poolPromise) return this._poolPromise;
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      this._poolPromise = fetch(`${root}?section_id=pulsio-search-pool`, { headers: { Accept: 'text/html' } })
        .then((r) => (r.ok ? r.text() : ''))
        .then((html) => new DOMParser().parseFromString(html, 'text/html').querySelector('[data-search-pool]'))
        .catch(() => null);
      return this._poolPromise;
    }

    async _intentResults(panel, intent) {
      const pool = await this._searchPool();
      if (!pool) return null;
      const isMobile = panel.classList.contains('pulsio-mobile-search');
      const wrap = document.createElement('div');
      wrap.setAttribute('data-search-results', '');
      const link = document.createElement('a');
      link.className = 'pulsio-search__intent';
      link.href = `${this._intentCentre || '/collections/all'}#${intent.tab}`;
      link.innerHTML = `<span>Shop all ${intent.label}</span><svg width="16" height="16" viewBox="0 0 28 28" fill="none" aria-hidden="true"><path d="M10.5 5.8335L18.6668 14L10.5 22.1668" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
      const title = document.createElement('h3');
      title.className = isMobile ? 'pulsio-mobile-search__section-title' : 'pulsio-search__section-title';
      title.textContent = intent.label;
      const list = document.createElement('div');
      list.className = isMobile ? 'pulsio-mobile-search__product-list' : 'pulsio-search__grid';
      intent.handles.forEach((h, i) => {
        const item = pool.querySelector(`[data-pool-handle="${h}"] ${isMobile ? '[data-pool-mobile]' : '[data-pool-desktop]'} > *`);
        if (!item) return;
        const node = item.cloneNode(true);
        node.style.setProperty('--index', i + 1);
        list.appendChild(node);
      });
      if (!list.children.length) return null;
      wrap.append(title, link, list);
      return wrap;
    }

    async runPredictiveSearch(panel, query) {
      if (!query || !panel) return;
      const state = panel.__pulsioSearch;
      if (!state || !state.container) return;

      const intent = this._matchIntent(query);
      if (intent) {
        const token = (state.token = (state.token || 0) + 1);
        const results = await this._intentResults(panel, intent);
        if (token !== state.token) return;
        if (results) { this.swapSearchResults(state, results); return; }
      }
      state.token = (state.token || 0) + 1;
      const fetchToken = state.token;

      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      // EU: keep the shopper's locale, or results come back in English / the default market.
      const url = `${root}search/suggest?q=${encodeURIComponent(query)}`
        + `&section_id=${encodeURIComponent(state.sectionId)}`
        + `&resources[type]=product&resources[limit]=6`;

      try {
        const res = await fetch(url, { headers: { 'Accept': 'text/html' } });
        if (!res.ok) return;
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-search-results]');
        if (!fresh || fetchToken !== state.token) return;

        if (!this.hasSearchProductResults(fresh)) {
          const fallback = this.getCategorySearchFallback(panel, query);
          if (fallback && this.hasSearchProductResults(fallback)) {
            this.swapSearchResults(state, fallback);
            return;
          }
        }

        this.swapSearchResults(state, fresh);
      } catch (err) {
        // Network errors leave the previous results in place.
      }
    }

    restorePopularResults(panel) {
      if (!panel) return;
      const state = panel.__pulsioSearch;
      if (!state || !state.container || !state.popularSnapshot) return;
      const current = state.container.querySelector('[data-search-results]');
      if (current) current.replaceWith(state.popularSnapshot.cloneNode(true));
    }

    /* ------------------------------------------------------------------
       Shop rail tabs (per-panel) — click to switch the active pane
       Uses delegated listener on the host so it works for all Shop panels.
       ------------------------------------------------------------------ */
    wireShopTabs() {
      const activate = (tab) => {
        const id = tab.getAttribute('data-shop-tab');
        const rail = tab.closest('.pulsio-shop__rail');
        const panes = rail && rail.parentElement.querySelectorAll('[data-shop-pane]');
        if (!panes) return;

        rail.querySelectorAll('[data-shop-tab]').forEach((t) => {
          const active = t === tab;
          t.setAttribute('aria-selected', active ? 'true' : 'false');
          t.setAttribute('tabindex', active ? '0' : '-1');
        });
        panes.forEach((p) => {
          p.hidden = p.getAttribute('data-shop-pane') !== id;
        });
      };

      this.addEventListener('click', (e) => {
        const tab = e.target.closest('[data-shop-tab]');
        if (!tab) return;
        e.preventDefault();
        activate(tab);
      });

      // Hovering a category shows it too (Justyn 17/10/2026 "change with hover"), on screens with a real
      // pointer. A short pause first, so sweeping the mouse across the rail on the way to a product
      // doesn't flick through every category.
      const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
      let hoverTimer = null;
      this.addEventListener('mouseover', (e) => {
        if (!canHover.matches) return;
        const tab = e.target.closest('[data-shop-tab]');
        clearTimeout(hoverTimer);
        if (!tab || tab.getAttribute('aria-selected') === 'true') return;
        hoverTimer = setTimeout(() => activate(tab), 120);
      });
      this.addEventListener('mouseout', (e) => {
        if (e.target.closest('[data-shop-tab]')) clearTimeout(hoverTimer);
      });

      // Keyboard: Arrow up/down cycles tabs within the active rail
      this.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Home' && e.key !== 'End') return;
        const tab = e.target.closest('[data-shop-tab]');
        if (!tab) return;
        const rail = tab.closest('.pulsio-shop__rail');
        const tabs = Array.from(rail.querySelectorAll('[data-shop-tab]'));
        const idx = tabs.indexOf(tab);
        let next = idx;
        if (e.key === 'ArrowDown') next = (idx + 1) % tabs.length;
        else if (e.key === 'ArrowUp') next = (idx - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = tabs.length - 1;
        e.preventDefault();
        tabs[next].focus();
        tabs[next].click();
      });
    }

    /* ------------------------------------------------------------------
       Mobile drawer accordion — intercepts the native <details> toggle to
       slide the body height open/closed. Without JS the browser flips the
       body between display:none and full height instantly, with no chance
       for a CSS height transition to fire. Open/close live as instance
       methods so the pills row can drive the same animation.
       ------------------------------------------------------------------ */
    _drawerAccClose(acc) {
      const body = acc.querySelector('.pulsio-drawer-acc__body');
      if (!body || !acc.open) return;
      body.dataset.animating = 'true';
      body.dataset.closing = 'true';
      body.style.height = body.offsetHeight + 'px';
      void body.offsetHeight;
      const onEnd = (ev) => {
        if (ev.target !== body || ev.propertyName !== 'height') return;
        body.removeEventListener('transitionend', onEnd);
        acc.open = false;
        body.style.height = '';
        delete body.dataset.closing;
        delete body.dataset.animating;
      };
      body.addEventListener('transitionend', onEnd);
      requestAnimationFrame(() => { body.style.height = '0px'; });
    }

    _drawerAccOpen(acc) {
      const body = acc.querySelector('.pulsio-drawer-acc__body');
      if (!body) return;
      body.dataset.animating = 'true';
      acc.open = true;
      body.style.height = '0px';
      void body.offsetHeight;
      const onEnd = (ev) => {
        if (ev.target !== body || ev.propertyName !== 'height') return;
        body.removeEventListener('transitionend', onEnd);
        body.style.height = '';
        delete body.dataset.animating;
      };
      body.addEventListener('transitionend', onEnd);
      requestAnimationFrame(() => {
        body.style.height = body.scrollHeight + 'px';
      });
    }

    wireDrawerAccordions() {
      if (!this.mobileDrawer) return;

      this.mobileDrawer.addEventListener('click', (e) => {
        const summary = e.target.closest('.pulsio-drawer-acc__head');
        if (!summary) return;
        const acc = summary.closest('.pulsio-drawer-acc');
        if (!acc) return;

        e.preventDefault();

        if (acc.open) {
          this._drawerAccClose(acc);
          this._syncDrawerPills(null);
          return;
        }

        // Single-open accordion — collapse any open sibling in the same
        // group before opening the clicked one.
        const group = acc.parentElement;
        if (group) {
          group.querySelectorAll('.pulsio-drawer-acc[open]').forEach((sib) => {
            if (sib !== acc) this._drawerAccClose(sib);
          });
        }
        this._drawerAccOpen(acc);
        this._syncDrawerPills(acc.getAttribute('data-drawer-acc'));
      });
    }

    /* ------------------------------------------------------------------
       Region & language sheet — opened from the £ button in the bar.
       Renders flag emojis from the data-iso codes on each row, handles
       Show all expansion, and toggles the sheet via its [hidden] attr.
       ------------------------------------------------------------------ */
    wireRegionSheet() {
      const sheet = this.querySelector('#pulsio-region-sheet');
      const triggers = this.querySelectorAll('[data-region-trigger]');
      if (!sheet || triggers.length === 0) return;

      // Convert ISO country codes to flag emojis. Regional Indicator Symbols
      // start at U+1F1E6 (A) — code = 127397 + ASCII code of the letter.
      sheet.querySelectorAll('.pulsio-region-sheet__flag[data-iso]').forEach((el) => {
        const iso = (el.dataset.iso || '').toUpperCase();
        if (iso.length !== 2) return;
        const codePoints = [
          127397 + iso.charCodeAt(0),
          127397 + iso.charCodeAt(1),
        ];
        el.textContent = String.fromCodePoint(...codePoints);
      });

      // Drawer slide-in is driven by data-region-open via CSS transitions
      // (transform + visibility). On open we drop [hidden] first, then on
      // the next frame set data-region-open so the browser registers the
      // pre-open transform before transitioning. On close we remove
      // data-region-open immediately so the slide-out runs, then reapply
      // [hidden] once the 500ms animation has settled.
      let lastTrigger = null;

      const open = (sourceTrigger) => {
        if (sourceTrigger) lastTrigger = sourceTrigger;
        clearTimeout(this._regionHideTimer);
        sheet.hidden = false;
        // Force a layout read so the pre-open transform is committed before
        // we flip the attribute — without this Chrome occasionally skips
        // the transition on the very first open.
        void sheet.offsetWidth;
        requestAnimationFrame(() => {
          sheet.setAttribute('data-region-open', 'true');
        });
        triggers.forEach((t) => t.setAttribute('aria-expanded', 'true'));
        this._lockScroll();
        this.showBackdrop(true);
        // Focus the close button so Esc + Tab work immediately.
        const closeBtn = sheet.querySelector('[data-region-close]');
        if (closeBtn) closeBtn.focus();
      };

      const close = () => {
        if (!sheet.hasAttribute('data-region-open') && sheet.hidden) return;
        sheet.removeAttribute('data-region-open');
        triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
        // Only release body scroll + backdrop if no other overlay needs them.
        if (!this.hasAttribute('data-mobile-state')) {
          this._unlockScroll();
        }
        if (!this.openMenuId
            && !this.hasAttribute('data-mobile-state')
            && (this.searchPanel ? this.searchPanel.hidden : true)) {
          this.showBackdrop(false);
        }
        clearTimeout(this._regionHideTimer);
        this._regionHideTimer = setTimeout(() => {
          sheet.hidden = true;
        }, 500);
        if (lastTrigger && document.body.contains(lastTrigger)) lastTrigger.focus();
      };

      triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => open(trigger));
      });

      sheet.addEventListener('click', (e) => {
        if (e.target.closest('[data-region-close]')) {
          e.preventDefault();
          close();
          return;
        }

        if (e.target.closest('[data-region-show-all]')) {
          e.preventDefault();
          sheet.querySelectorAll('[data-region-hidden]').forEach((row) => {
            row.removeAttribute('data-region-hidden');
          });
          e.target.closest('[data-region-show-all]').hidden = true;
        }
      });

      // Esc closes the sheet.
      sheet.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && sheet.hasAttribute('data-region-open')) close();
      });

      this._closeRegionSheet = close;
    }

    /* ------------------------------------------------------------------
       Mobile drawer category pills — horizontally scrollable shortcuts in
       the sticky band under the white bar. Tapping a pill opens that
       category's accordion (single-open) and scrolls it into view; the
       active pill tracks whichever accordion is open, including manual
       summary taps.
       ------------------------------------------------------------------ */
    /* Horizontal glide for the pills scroller. Native behavior:'smooth' is
       unreliable here (it silently no-ops in some engines when the drawer
       is mid-animation), so drive a short ease-out on rAF instead; jumps
       straight there when motion is reduced. */
    _glideScroller(scroller, target, animate) {
      if (this._glideRaf) cancelAnimationFrame(this._glideRaf);
      const start = scroller.scrollLeft;
      const delta = target - start;
      if (!animate || Math.abs(delta) < 1) { scroller.scrollLeft = target; return; }
      const dur = 280;
      const t0 = performance.now();
      let done = false;
      const step = (now) => {
        const p = Math.min(1, (now - t0) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        scroller.scrollLeft = start + delta * eased;
        if (p < 1) this._glideRaf = requestAnimationFrame(step);
        else done = true;
      };
      this._glideRaf = requestAnimationFrame(step);
      // Frames stop in hidden/throttled documents; if the glide has not
      // finished shortly after it should have, land the row anyway.
      clearTimeout(this._glideFallback);
      this._glideFallback = setTimeout(() => {
        if (!done) { cancelAnimationFrame(this._glideRaf); scroller.scrollLeft = target; }
      }, dur + 120);
    }

    _syncDrawerPills(activeId) {
      if (!this.mobileDrawer) return;
      const smooth = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      this.mobileDrawer.querySelectorAll('[data-drawer-pill]').forEach((pill) => {
        const active = activeId != null && pill.getAttribute('data-drawer-pill') === activeId;
        pill.setAttribute('data-active', active ? 'true' : 'false');
        pill.setAttribute('aria-expanded', active ? 'true' : 'false');
        // Park the active pill at the left edge of the scroller (inside its
        // scroll-padding inset) so the following categories peek in from the
        // right and invite a swipe. Horizontal only — the drawer's vertical
        // scroll is handled separately by the caller.
        if (active) {
          const scroller = pill.closest('.pulsio-drawer__pills');
          if (scroller) {
            const inset = parseFloat(getComputedStyle(scroller).scrollPaddingInlineStart) || 16;
            const left = scroller.scrollLeft
              + (pill.getBoundingClientRect().left - scroller.getBoundingClientRect().left)
              - inset;
            this._glideScroller(scroller, Math.max(0, left), smooth === 'smooth');
          }
        }
      });
    }

    // Phone search box in the top row (Justyn 17/10/2026). It sits in the bar while the menu or search is open;
    // a tap switches the menu to search in place (no new row, the keyboard opens on iPhone), and typing feeds
    // the phone search panel's own input so its results, phrase matching and Clear all keep working.
    wireInlineSearch() {
      const box = this.querySelector('[data-inline-search-input]');
      if (!box || !this.mobileSearch) return;
      this.inlineSearch = box;
      const panelInput = this.mobileSearch.querySelector('[data-search-input]');
      const form = this.mobileSearch.querySelector('[data-search-form]');
      box.addEventListener('focus', () => {
        if (this.getAttribute('data-mobile-state') === 'drawer-open') {
          if (this.mobileDrawer) this.mobileDrawer.hidden = true;
          this.openMobileSearch({ keepFocus: true });
        }
      });
      box.addEventListener('input', () => {
        if (!panelInput) return;
        panelInput.value = box.value;
        panelInput.dispatchEvent(new Event('input', { bubbles: true }));
      });
      box.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' || !form) return;
        e.preventDefault();
        if (panelInput) panelInput.value = box.value;
        form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true }));
        box.blur();
      });
    }

    wireDrawerPills() {
      if (!this.mobileDrawer) return;
      const drawer = this.mobileDrawer.querySelector('.pulsio-drawer');
      if (!drawer) return;

      // Phones (Justyn 17/10/2026): one product list at a time under the pills. Best Sellers is the default
      // and has no pill; tapping a pill swaps in that category's list, tapping the active pill again goes
      // back to Best Sellers. Each swap returns the drawer to the top of the list, so the sticky bar and
      // pill row never cover the first products (they used to, when a category opened lower down and was
      // scrolled to under the sticky bars).
      drawer.addEventListener('click', (e) => {
        const pill = e.target.closest('[data-drawer-pill]');
        if (!pill) return;
        e.preventDefault();
        const pane = pill.closest('[data-drawer-pane]') || drawer;
        const wasActive = pill.getAttribute('data-active') === 'true';
        const fallback = pane.querySelector('[data-drawer-default]');
        const target = wasActive ? fallback : pane.querySelector(`[data-drawer-acc="${pill.getAttribute('data-drawer-pill')}"]`);
        if (!target) return;
        this._showDrawerList(pane, target);
        this._syncDrawerPills(wasActive ? null : pill.getAttribute('data-drawer-pill'));
      });

      // Tapping the Shop tab always goes back to Best Sellers, even when Shop is already showing a category
      // (Justyn 17/10/2026).
      drawer.addEventListener('click', (e) => {
        if (!e.target.closest('[data-drawer-master="shop"]')) return;
        const pane = drawer.querySelector('[data-drawer-pane="shop"]');
        const fallback = pane && pane.querySelector('[data-drawer-default]');
        if (!fallback) return;
        this._showDrawerList(pane, fallback, true);
        this._syncDrawerPills(null);
      });

      drawer.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Home' && e.key !== 'End') return;
        const pill = e.target.closest('[data-drawer-pill]');
        if (!pill) return;
        const pills = Array.from(drawer.querySelectorAll('[data-drawer-pill]'));
        const idx = pills.indexOf(pill);
        let next = idx;
        if (e.key === 'ArrowRight') next = (idx + 1) % pills.length;
        else if (e.key === 'ArrowLeft') next = (idx - 1 + pills.length) % pills.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = pills.length - 1;
        e.preventDefault();
        pills[next].focus();
        pills[next].click();
      });
    }

    /* ------------------------------------------------------------------
       Scroll state — sets data-scroll-state on the host:
         "top"      → at the very top, transparent w/ gradient
         "scrolled" → past threshold, pill-shape collapsed bar
         "hidden"   → scrolled down past threshold, bar tucked off-screen
       Auto-restores when a panel is open or while scrolling up.
       ------------------------------------------------------------------ */
    wireScrollState() {
      let lastY = window.scrollY;
      let ticking = false;
      const threshold = 24;
      const hideAfter = 120;

      const docEl = document.documentElement;

      const onScroll = () => {
        const y = window.scrollY;
        const dy = y - lastY;

        const panelOpen = this.hasAttribute('data-panel-open');
        const searchOpen = this.hasAttribute('data-search-open');

        // While the mega menu is open, freeze the bar's scroll-driven state
        // and translate so it stays exactly where it was when the user
        // opened the menu (matching the top-state behaviour where nothing
        // moves on open/close). lastY still advances so the next scroll
        // tick after close doesn't see a giant dy and trigger hide/scrolled.
        // Same while an overlay pins the page (and for the frame it is put back): pinning jumps scrollY to 0 and
        // unpinning jumps it back, which otherwise flipped the bar to "top" and then to "hidden" with an animated
        // resize just after the phone menu closed (17/10/2026, measured as five slow frames in iPhone WebKit).
        if (panelOpen || this._scrollLocked || this._scrollRestoring) {
          lastY = y;
          ticking = false;
          return;
        }

        // Search still forces full-width "top" so the overlay attaches
        // cleanly below the bar.
        if (searchOpen || y <= threshold) {
          this.setAttribute('data-scroll-state', 'top');
        } else if (dy > 4 && y > hideAfter) {
          this.setAttribute('data-scroll-state', 'hidden');
        } else if (dy < -4) {
          this.setAttribute('data-scroll-state', 'scrolled');
        }

        // Track the actual page scroll position independent of panel state so
        // CSS can differentiate "menu open at top" from "menu open while scrolled".
        if (y > threshold) {
          this.setAttribute('data-page-scrolled', 'true');
        } else {
          this.removeAttribute('data-page-scrolled');
        }

        // Slide the bar up with the non-sticky announcement bar so there's
        // no gap once it scrolls past viewport top.
        const annHeight = parseFloat(getComputedStyle(docEl).getPropertyValue('--announcement-bar-height')) || 0;
        this.style.setProperty('--pulsio-bar-translate', `${-Math.min(annHeight, y)}px`);

        lastY = y;
        ticking = false;
      };

      window.addEventListener('scroll', () => {
        if (!ticking) {
          requestAnimationFrame(onScroll);
          ticking = true;
        }
      }, { passive: true });

      // Initial state
      onScroll();
    }

    /* ------------------------------------------------------------------
       Top-state spacer sizing — publishes the fixed bar's measured height
       as --pulsio-top-reserve on <html> so the .pulsio-top-reserve spacer
       (rendered on solid "dark" nav pages: product + index) reserves exactly
       the bar's footprint at the top on mobile. The spacer is non-sticky, so
       it scrolls away and the bar resumes overlapping content with no jump.
       Only present on those pages; a no-op elsewhere (nothing reads the var).
       ------------------------------------------------------------------ */
    wireTopReserve() {
      const docEl = document.documentElement;
      const spacer = document.querySelector('.pulsio-top-reserve');
      if (!spacer || !this.bar) return;

      const update = () => {
        const barH = this.bar.getBoundingClientRect().height;
        if (barH <= 0) return;
        // Reserve the fixed bar's footprint measured to its ACTUAL bottom edge,
        // not just its height. The bar is offset from the top by its CSS `top`
        // (= --announcement-bar-height, which is rounded), while the spacer sits
        // in normal flow directly below the announcement bar's REAL (sub-pixel)
        // height. Those two differ by up to ~2px, so reserving only the bar's
        // height left a thin gap between the bar's bottom and the content top
        // where the black page background showed through (the "black line" seam
        // on mobile). Compute reserve = barTop + barH - realAnnHeight and floor
        // it so content tucks a hair under the bar's bottom instead of leaving a
        // gap. Falls back to the bar height if the announcement bar isn't found.
        const barTop = parseFloat(getComputedStyle(this.bar).top) || 0;
        const annEl = document.querySelector('.shopify-section--announcement-bar');
        const annH = annEl ? annEl.getBoundingClientRect().height : barTop;
        let reserve = barTop + barH - annH;
        if (!(reserve > 0)) reserve = barH;
        docEl.style.setProperty('--pulsio-top-reserve', `${Math.floor(reserve)}px`);
      };

      update();
      // Re-measure after fonts/layout settle and on resize/orientation change.
      requestAnimationFrame(update);
      window.addEventListener('resize', update, { passive: true });
      if (window.ResizeObserver) {
        this._topReserveRO = new ResizeObserver(update);
        this._topReserveRO.observe(this.bar);
      }
    }

    /* ------------------------------------------------------------------
       Cart icon active pill — observes cart-drawer open state
       ------------------------------------------------------------------ */
    wireCartObserver() {
      if (!this.cartLink) return;
      const drawer = document.getElementById('cart-drawer');
      if (!drawer) return;
      const sync = () => {
        const isOpen = drawer.hasAttribute('open') || drawer.classList.contains('is-open');
        this.cartLink.setAttribute('data-cart-active', isOpen ? 'true' : 'false');
      };
      const mo = new MutationObserver(sync);
      mo.observe(drawer, { attributes: true, attributeFilter: ['open', 'class'] });
      sync();
    }

    /* ------------------------------------------------------------------
       Live cart count / bag-state — switches the cart icon between the
       empty (outline) and filled (solid white + number) states without a
       page reload. Both bags + <cart-count> are always in the DOM; we only
       toggle data-cart-has-items on the cart link and write the number.

       Sources, most specific first:
       - The theme's own cart DOM events (cart:change on add, line-item:change
         on qty edits) carry the fresh cart in event.detail.cart.
       - A universal fallback intercepts ANY cart mutation (fetch OR XHR to
         /cart/add|change|update|clear) — this covers the theme ProductForm,
         the custom cart-drawer-v2.js, upsells and the jQuery product pages —
         then reads the authoritative item_count from /cart.js.
       ------------------------------------------------------------------ */
    wireCartCount() {
      const cart = this.cartLink;
      if (!cart) return;
      this._cartCountEl = cart.querySelector('.pulsio-header__cart-count');

      const apply = (count) => {
        const n = Math.max(0, parseInt(count, 10) || 0);
        cart.setAttribute('data-cart-has-items', n > 0 ? 'true' : 'false');
        if (this._cartCountEl && String(n) !== String(this._cartCountEl.textContent).trim()) {
          this._cartCountEl.textContent = n;
        }
      };
      this._applyCartCount = apply;

      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      let refreshTimer;
      const refresh = () => {
        clearTimeout(refreshTimer);
        refreshTimer = setTimeout(() => {
          fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
            .then((r) => (r.ok ? r.json() : null))
            .then((c) => { if (c) apply(c.item_count); })
            .catch(() => {});
        }, 120);
      };

      const fromEvent = (e) => {
        const c = e && e.detail && e.detail.cart;
        if (c && typeof c.item_count === 'number') apply(c.item_count);
        else refresh();
      };
      document.addEventListener('cart:change', fromEvent);
      document.addEventListener('line-item:change', fromEvent);
      document.addEventListener('cart:refresh', refresh);
      window.addEventListener('pageshow', (e) => { if (e.persisted) refresh(); });

      this._installCartMutationHooks(refresh);
    }

    /* Patch fetch + XMLHttpRequest once (globally) so every cart mutation —
       regardless of which script issues it — triggers a refresh. Patches chain
       safely with other wrappers (e.g. cart-drawer-v2.js) since each captures
       and calls the previous implementation. */
    _installCartMutationHooks(refresh) {
      const isCartMutation = (url) =>
        typeof url === 'string' && /\/cart\/(add|change|update|clear)(\.js)?(\?|#|$)/.test(url);

      if (window.__pulsioCartHooked) {
        (window.__pulsioCartRefreshers = window.__pulsioCartRefreshers || []).push(refresh);
        return;
      }
      window.__pulsioCartHooked = true;
      window.__pulsioCartRefreshers = [refresh];
      const fire = () => {
        (window.__pulsioCartRefreshers || []).forEach((fn) => { try { fn(); } catch (e) {} });
      };

      const origFetch = window.fetch;
      if (typeof origFetch === 'function') {
        window.fetch = function (input, init) {
          const url = typeof input === 'string' ? input : (input && input.url) || '';
          const p = origFetch.apply(this, arguments);
          if (isCartMutation(url) && p && typeof p.then === 'function') {
            p.then(() => fire()).catch(() => {});
          }
          return p;
        };
      }

      const XHR = window.XMLHttpRequest;
      if (XHR && XHR.prototype) {
        const origOpen = XHR.prototype.open;
        const origSend = XHR.prototype.send;
        XHR.prototype.open = function (method, url) {
          this.__pulsioCartUrl = url;
          return origOpen.apply(this, arguments);
        };
        XHR.prototype.send = function () {
          if (isCartMutation(this.__pulsioCartUrl)) {
            this.addEventListener('load', () => fire());
          }
          return origSend.apply(this, arguments);
        };
      }
    }

    /* ------------------------------------------------------------------
       Backdrop + global handlers
       ------------------------------------------------------------------ */
    showBackdrop(visible) {
      if (this.backdrop) {
        if (visible) {
          this.backdrop.hidden = false;
          requestAnimationFrame(() => this.backdrop.setAttribute('data-visible', 'true'));
        } else {
          this.backdrop.removeAttribute('data-visible');
          setTimeout(() => { if (this.backdrop) this.backdrop.hidden = true; }, 200);
        }
      }
    }

    wireBackdrop() {
      if (!this.backdrop) return;
      this.backdrop.addEventListener('click', () => {
        this.closeMenu();
        this.closeMobile();
        this.closeSearch();
        if (this._closeRegionSheet) this._closeRegionSheet();
        this.showBackdrop(false);
      });
    }

    onDocClick(e) {
      const menuOpen = !!this.openMenuId;
      const searchOpen = this.searchPanel ? !this.searchPanel.hidden : false;
      if (!menuOpen && !searchOpen) return;

      // Click landed fully outside the header → close everything (original behaviour).
      if (!this.contains(e.target)) {
        this.closeMenu();
        this.closeSearch();
        this.showBackdrop(false);
        return;
      }

      // Inside the header. When a mega-menu panel is open, treat a click on the
      // top bar strip (its empty space / the area above the nav links) as
      // "outside" and dismiss the panel — the bar covers most of the viewport
      // top at ~1440px, so this gives an easy escape without hunting for the
      // sliver of exposed backdrop. Menu triggers (which toggle) and the other
      // bar controls (search / cart / region / account / logo / the close X)
      // run their own handlers, so they're excluded here. Clicks inside the
      // open panel content are left alone so the menu stays put while browsing.
      if (menuOpen && this.bar && this.bar.contains(e.target)) {
        const selfHandling = e.target.closest(
          '[data-menu-trigger],[data-search-trigger],[data-cart-trigger],'
          + '[data-region-trigger],'
          + '.pulsio-header__account,.pulsio-header__logo,.pulsio-header__hamburger,'
          + '.pulsio-header__nav-link'
        );
        if (!selfHandling) this.closeMenu();
      }
    }

    onKey(e) {
      if (e.key !== 'Escape') return;
      this.closeMenu();
      this.closeMobile();
      this.closeSearch();
      this.showBackdrop(false);
    }
  }

  customElements.define('pulsio-header', PulsioHeader);
})();

// Shop menu product area (Justyn 20/10/2026: "I don't want it cut off at all" / "resize the boxes and images to make
// it fit"). Categories that fit are left exactly as designed. When the shown category has more rows than fit the
// menu, its product grid is scaled down (CSS zoom, so images, text and spacing shrink together) just enough for every
// row to fit, never below 70%, with one or two extra columns tried too (fewer rows need less shrinking; cards never
// narrower than 250px of their own width, and never so narrow that more names or descriptions get cut short with
// "..." than in the designed columns; on the very shortest screens 60% with whole names beats 70% with cut ones). Only if that would still go below 80% does the pane make room: two photo
// tiles stack in one column (step 1), then the photo tiles step aside (step 2). If nothing fits even at 70%, the
// pane's "View all" link steps aside too (step 3; Shop All stays in the footer strip).
// The homepage scales its whole layout up past 1728px wide, so the menu's height caps are divided by that scale
// (--pulsio-page-scale) to keep the menu inside the screen.
(function () {
  var MIN = 0.7;
  var LAST = 0.6;
  var GOOD = 0.8;
  var MIN_CARD = 250;
  function visiblePane(panes) {
    return Array.prototype.find.call(panes.querySelectorAll('.pulsio-shop__pane'), function (el) { return el.offsetHeight > 0; });
  }
  function fits(panes) { return panes.scrollHeight <= panes.clientHeight + 1; }
  // names and descriptions cut short ("...") by their line clamp
  function clipped(grid) {
    return Array.prototype.filter.call(grid.querySelectorAll('.pulsio-card__title, .pulsio-card__sub'), function (el) {
      return el.offsetHeight && (el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1);
    }).length;
  }
  function setLayout(pane, grid, step, cols, base, zoom) {
    pane.classList.toggle('is-fit-stacked', step === 1);
    pane.classList.toggle('is-fit-no-tiles', step >= 2);
    pane.classList.toggle('is-fit-no-link', step === 3);
    grid.style.gridTemplateColumns = cols ? 'repeat(' + cols + ', minmax(0, 1fr))' : '';
    grid.style.zoom = zoom === 1 ? '' : String(zoom);
  }
  // Best layout for one step: the most columns-and-size combination that fits, or null. Strict: no more names or
  // descriptions cut short than in the step's own columns at full size (fewer rows are no use if names lose words).
  function bestFor(panes, pane, grid, step, strict, floor) {
    setLayout(pane, grid, step, 0, 0, 1);
    var cs = getComputedStyle(grid);
    var base = cs.gridTemplateColumns.split(' ').filter(Boolean).length || 1;
    if (fits(panes)) return { step: step, cols: 0, zoom: 1 };
    var allowed = strict ? clipped(grid) : Infinity;
    var ok = function () { return fits(panes) && clipped(grid) <= allowed; };
    var gap = parseFloat(cs.columnGap) || 0;
    var width = grid.getBoundingClientRect().width;
    var best = null;
    for (var cols = base; cols <= base + 2; cols++) {
      // the narrowest a card may get, in its own (unscaled) width, even at the smallest size
      if (cols > base && (width / floor - gap * (cols - 1)) / cols < MIN_CARD) break;
      setLayout(pane, grid, step, cols, base, floor);
      if (!ok()) continue;
      var lo = floor, hi = 1;
      for (var i = 0; i < 7; i++) {
        var mid = (lo + hi) / 2;
        setLayout(pane, grid, step, cols, base, mid);
        if (ok()) lo = mid; else hi = mid;
      }
      if (cols > base && (width / lo - gap * (cols - 1)) / cols < MIN_CARD) continue;
      if (!best || lo > best.zoom + 0.04) best = { step: step, cols: cols === base ? 0 : cols, zoom: lo };
    }
    return best;
  }
  function choose(panes, pane, grid, steps, strict, floor) {
    var chosen = null;
    for (var k = 0; k < steps.length; k++) {
      if (steps[k] === 3 && chosen) break;
      var b = bestFor(panes, pane, grid, steps[k], strict, floor);
      if (b && (!chosen || b.zoom > chosen.zoom + 0.04)) chosen = b;
      if (chosen && chosen.zoom >= GOOD) break;
    }
    return chosen;
  }
  function fit(panes) {
    if (!window.matchMedia('(min-width: 1024px)').matches) {
      Array.prototype.forEach.call(panes.querySelectorAll('.pulsio-shop__pane'), function (pane) {
        var grid = pane.querySelector('.pulsio-shop-pane__grid');
        if (grid) setLayout(pane, grid, 0, 0, 0, 1);
      });
      return;
    }
    if (!panes.offsetHeight) return;
    var pane = visiblePane(panes);
    var grid = pane && pane.querySelector('.pulsio-shop-pane__grid');
    if (!grid) return;
    var hasTiles = !pane.matches('.pulsio-shop__pane--three-col, .pulsio-shop__pane--no-tiles');
    var steps = !hasTiles ? [0, 3] : pane.classList.contains('pulsio-shop__pane--two-tiles') ? [0, 1, 2, 3] : [0, 2, 3];
    // last resorts on very short screens: a little smaller still (60%) with every name whole, then names cut short
    var chosen = choose(panes, pane, grid, steps, true, MIN) || choose(panes, pane, grid, steps, true, LAST) ||
      choose(panes, pane, grid, steps, false, MIN);
    if (!chosen) chosen = { step: steps[steps.length - 1], cols: 0, zoom: MIN };
    setLayout(pane, grid, chosen.step, chosen.cols, 0, Math.floor(chosen.zoom * 100) / 100);
  }
  function setScale(shop) {
    var w = shop.offsetWidth;
    var scale = w ? Math.round(shop.getBoundingClientRect().width / w * 1000) / 1000 : 1;
    if (scale > 1.001) shop.style.setProperty('--pulsio-page-scale', String(scale));
    else shop.style.removeProperty('--pulsio-page-scale');
  }
  function bind(panes) {
    if (panes.__fitBound) return;
    panes.__fitBound = true;
    var shop = panes.closest('.pulsio-shop');
    var queued = false;
    var schedule = function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        if (shop && shop.offsetHeight) setScale(shop);
        fit(panes);
      });
    };
    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(schedule);
      ro.observe(panes);
      Array.prototype.forEach.call(panes.querySelectorAll('.pulsio-shop__pane'), function (el) { ro.observe(el); });
    }
    window.addEventListener('resize', schedule);
    schedule();
  }
  function init() { document.querySelectorAll('.pulsio-shop__panes').forEach(bind); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
